import { useRef } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import ReactCrop, { Crop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';

interface ImageCropModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  imageSrc: string;
  crop: Crop | undefined;
  setCrop: (crop: Crop | undefined) => void;
  completedCrop: Crop | undefined;
  setCompletedCrop: (crop: Crop | undefined) => void;
  croppingFor: 'profile' | 'logo';
  onImageLoad: (e: React.SyntheticEvent<HTMLImageElement>) => void;
  onSave: () => void;
  onCancel: () => void;
}

export default function ImageCropModal({
  open,
  onOpenChange,
  imageSrc,
  crop,
  setCrop,
  completedCrop,
  setCompletedCrop,
  croppingFor,
  onImageLoad,
  onSave,
  onCancel
}: ImageCropModalProps) {
  const imageRef = useRef<HTMLImageElement>(null);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            Crop {croppingFor === 'profile' ? 'Profile Picture' : 'Company Logo'}
          </DialogTitle>
          {croppingFor === 'logo' && (
            <p className="text-sm text-neutral-600 mt-2">
              Adjust the selection box to fit your logo. The logo will maintain its proportions and transparency.
            </p>
          )}
        </DialogHeader>

        <div className="space-y-4">
          {imageSrc && (
            <div className="flex justify-center">
              <ReactCrop
                crop={crop}
                onChange={(_, percentCrop) => setCrop(percentCrop)}
                onComplete={(c) => setCompletedCrop(c)}
                aspect={croppingFor === 'profile' ? 1 : undefined}
                className="max-w-full"
              >
                <img
                  ref={imageRef}
                  src={imageSrc}
                  onLoad={onImageLoad}
                  style={{ maxWidth: '100%', maxHeight: '400px' }}
                  alt="Crop preview"
                />
              </ReactCrop>
            </div>
          )}

          <div className="flex justify-end space-x-2">
            <Button variant="outline" onClick={onCancel}>
              Cancel
            </Button>
            <Button
              onClick={onSave}
              disabled={!completedCrop}
              className="btn-max-primary"
            >
              Save Cropped Image
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
